import { env } from '@config/env';

interface Grecaptcha {
  ready(cb: () => void): void;
  execute(siteKey: string, options: { action: string }): Promise<string>;
}

declare global {
  interface Window {
    grecaptcha?: Grecaptcha;
  }
}

let loadPromise: Promise<void> | null = null;

// Cap how long we wait for Google's script. Without this a stalled request
// would leave loadPromise pending forever and hang forgot-password submit.
const RECAPTCHA_LOAD_TIMEOUT_MS = 10_000;

function loadScript(siteKey: string): Promise<void> {
  if (loadPromise) return loadPromise;
  loadPromise = new Promise<void>((resolve, reject) => {
    if (typeof document === 'undefined') {
      reject(new Error('document unavailable'));
      return;
    }
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
    script.async = true;
    script.defer = true;
    const timeoutId = setTimeout(() => {
      script.remove();
      loadPromise = null; // allow a retry on the next attempt
      reject(new Error('reCAPTCHA load timed out'));
    }, RECAPTCHA_LOAD_TIMEOUT_MS);
    script.onload = () => {
      clearTimeout(timeoutId);
      resolve();
    };
    script.onerror = () => {
      clearTimeout(timeoutId);
      loadPromise = null; // allow a retry on the next attempt
      reject(new Error('Failed to load reCAPTCHA'));
    };
    document.head.appendChild(script);
  });
  return loadPromise;
}

/**
 * Runs reCAPTCHA v3 for an action and returns a token, or null when reCAPTCHA
 * is disabled (VITE_CAPTCHA_ENABLED=false), isn't configured (no site key), or
 * fails to load. The token is attached to the request; server-side verification
 * (feature-flag-gated) does the enforcement.
 */
export async function executeRecaptcha(action: string): Promise<string | null> {
  // Master switch — when disabled, skip reCAPTCHA entirely so no token is sent.
  // Used to turn captcha off per environment and when running tests.
  if (!env.VITE_CAPTCHA_ENABLED) return null;
  const siteKey = env.VITE_CAPTCHA_SITE_KEY;
  if (!siteKey) return null;
  try {
    await loadScript(siteKey);
    const grecaptcha = window.grecaptcha;
    if (!grecaptcha) return null;
    await new Promise<void>((resolve) => grecaptcha.ready(resolve));
    return await grecaptcha.execute(siteKey, { action });
  } catch {
    return null;
  }
}
